/**
 * Placement des voitures live sur l’arc d’un virage groupé (Petit/Moyen/Large)
 * ou d’un virage 1-case. Partagé / testé hors de `circuit3dPreview.ts`.
 */

export interface CornerArcInfo {
  pivotX: number;
  pivotY: number;
  rotationSteps: number;
  armCells: number;
  offset: number;
  /**
   * Précalculé une fois par virage à partir du `facingSteps` du pivot — seule
   * case du virage dont `facingSteps` vaut réellement `stepOut` : les cases de
   * bras avant le pivot gardent le `facingSteps` de la direction d’ENTRÉE.
   */
  zeroIsEntry: boolean;
}

/**
 * Angle sur l’arc [0, π/2] pour la case `offset`.
 *
 * Chaque case du virage (bras + pivot) est ancrée au **centre** de son
 * segment d’arc — pas sur les bords. Sinon `offset = ±armCells` tombe pile sur
 * la jointure avec le virage voisin : deux voitures sur des cases distinctes
 * (fin d’un asset / début du suivant) se superposent en monde.
 */
export function arcTheta(info: CornerArcInfo): number {
  if (info.armCells <= 0) return Math.PI / 4;
  const cellCount = info.armCells * 2 + 1;
  const cellIndex = info.offset + info.armCells;
  const progress = (cellIndex + 0.5) / cellCount;
  return info.zeroIsEntry ? progress * (Math.PI / 2) : (1 - progress) * (Math.PI / 2);
}

/** Position grille (fractionnaire) sur l’arc réel (`buildProceduralCorner`). */
export function arcAdjustedGridPosition(info: CornerArcInfo): { x: number; y: number } {
  const radius = info.armCells + 0.5;
  const phi = -(info.rotationSteps * Math.PI) / 2;
  const theta = arcTheta(info);
  const localX = radius * (Math.cos(theta) - 1);
  const localZ = radius * (1 - Math.sin(theta));
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);
  return {
    x: info.pivotX + (localX * cosPhi + localZ * sinPhi),
    y: info.pivotY + (-localX * sinPhi + localZ * cosPhi),
  };
}

/** Sens de circulation sur l’arc (tangent), en delta grille. */
export function arcTravelForward(info: CornerArcInfo): { dx: number; dy: number } {
  const phi = -(info.rotationSteps * Math.PI) / 2;
  const theta = arcTheta(info);
  const dir = info.zeroIsEntry ? 1 : -1;
  const localTx = dir * -Math.sin(theta);
  const localTz = dir * -Math.cos(theta);
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);
  return {
    dx: localTx * cosPhi + localTz * sinPhi,
    dy: -localTx * sinPhi + localTz * cosPhi,
  };
}
